import {
  createAction,
  createAsyncThunk,
  createSelector,
  createSlice,
  injectSlice,
  PayloadAction,
} from '@betty/toolkit/state';
import {
  parseEvent,
  parseEvents,
  checkIfCommunityJackpotVisible,
} from './communityJackpot.utils';
import {
  CommunityJackpotState,
  CommunityEventMilestone,
  PlayerCommunityEventReward,
  PlayerCommunityEvent,
} from './communityJackpot.types';
import CommunityJackpotService from '../services/CommunityJackpotService/CommunityJackpotService';

const initialState: CommunityJackpotState = {
  playerCommuntiyEvents: [],
  playerCommunityEventRewards: [],
  dataState: 'idle',
};

export const openCommunityJackpot = createAction('communityJackpot/open');

export const fetchCommunityEvents = createAsyncThunk(
  'communityJackpot/fetchEvents',
  async () => await CommunityJackpotService.getCommunityEvents(),
);

export const claimCommunityEventRewards = createAsyncThunk(
  'communityJackpot/claimRewards',
  async () =>
    await CommunityJackpotService.claimCommunityEventRewards({
      data: {
        RewardType: 'CommunityEvent',
      },
    }),
);

const communityJackpot = createSlice({
  name: 'communityJackpot',
  initialState,
  reducers: {
    addReward(state, { payload }: PayloadAction<PlayerCommunityEventReward>) {
      state.playerCommunityEventRewards.push(payload);
    },
    updateEvent(state, { payload }: PayloadAction<PlayerCommunityEvent>) {
      const { id } = payload;
      const targetIndex = state.playerCommuntiyEvents.findIndex(
        (event) => event.id === id,
      );

      if (targetIndex !== -1) {
        state.playerCommuntiyEvents.splice(targetIndex, 1, parseEvent(payload));
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCommunityEvents.pending, (state) => {
      state.dataState = 'pending';
    });

    builder.addCase(fetchCommunityEvents.fulfilled, (state, { payload }) => {
      const { playerCommuntiyEvents, playerCommunityEventRewards } = payload;

      state.playerCommuntiyEvents = parseEvents(playerCommuntiyEvents);
      state.playerCommunityEventRewards = playerCommunityEventRewards;
      state.dataState = 'fulfilled';
    });

    builder.addCase(fetchCommunityEvents.rejected, (state) => {
      state.dataState = 'rejected';
    });

    builder.addCase(claimCommunityEventRewards.fulfilled, (state) => {
      state.playerCommunityEventRewards =
        initialState.playerCommunityEventRewards;
    });
  },
});

export const { addReward, updateEvent } = communityJackpot.actions;

export const withCommunityJackpot = injectSlice({
  reducerPath: 'communityJackpot',
  reducer: communityJackpot.reducer,
});

export const selectCommunityJackpotRewards = withCommunityJackpot.selector(
  (state) => state.communityJackpot.playerCommunityEventRewards,
);

export const selectCommunityJackpotEvents = withCommunityJackpot.selector(
  (state) => state.communityJackpot.playerCommuntiyEvents,
);

export const selectCommunityJackpotDataState = withCommunityJackpot.selector(
  (state) => state.communityJackpot.dataState,
);

export const selectCommunityJackpotMilestone = createSelector(
  [(_, id: CommunityEventMilestone['id']) => id, selectCommunityJackpotEvents],
  (id, [event]) => event.milestones.find((milestone) => milestone.id === id),
);

export const selectIsCommunityJackpotVisible = createSelector(
  [selectCommunityJackpotRewards, selectCommunityJackpotEvents],
  (playerRewards, [playerEvent]) =>
    checkIfCommunityJackpotVisible(playerRewards, playerEvent),
);

export const selectCommunityJackpotMilestones = createSelector(
  selectCommunityJackpotEvents,
  (data) => {
    const eventData = data[0];
    if (!eventData) return [];
    return eventData.milestones;
  },
);

export const selectCommunityJackpotImages = createSelector(
  [(state) => state.communityJackpot.playerCommuntiyEvents],
  (data) => {
    const event = data[0];
    if (!event) return {};
    const {
      titleImageUrl,
      progressBarBackgroundUrl,
      progressBarTotalContributionsHolderUrl,
      milestoneReachedGlowUrl,
      rewardWonUrl,
      jackpotWonUrl,
      tnCPageSwiperPhoneOneWebUrl,
      tnCPageSwiperPhoneTwoWebUrl,
      tnCPageSwiperPhoneThreeWebUrl,
      tnCPageSwiperPhoneOneMobileUrl,
      tnCPageSwiperPhoneTwoMobileUrl,
      tnCPageSwiperPhoneThreeMobileUrl,
      tnCPageMeterUrl,
    } = event;
    return {
      titleImageUrl,
      progressBarBackgroundUrl,
      progressBarTotalContributionsHolderUrl,
      milestoneReachedGlowUrl,
      rewardWonUrl,
      jackpotWonUrl,
      tnCPageSwiperPhoneOneWebUrl,
      tnCPageSwiperPhoneTwoWebUrl,
      tnCPageSwiperPhoneThreeWebUrl,
      tnCPageSwiperPhoneOneMobileUrl,
      tnCPageSwiperPhoneTwoMobileUrl,
      tnCPageSwiperPhoneThreeMobileUrl,
      tnCPageMeterUrl,
    };
  },
);

declare global {
  interface LazyLoadedSlices {
    communityJackpot: ReturnType<typeof communityJackpot.reducer>;
  }
}
